import {
    type ComponentType as ReactComponent,
    type FC,
    type MouseEvent,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { DndProvider } from "react-dnd";
import { TouchBackend } from "react-dnd-touch-backend";
import { useMediaQuery } from "@onshopfront/components";
import { useShopfrontDispatch } from "../../../_Bootstrap/hooks";
import { showDialog } from "../../../Actions/Dialogs/DialogActions";
import { CancelOptionButton } from "../../Buttons/Button";
import { usePrompt } from "../../Dialogs/NavigationBlock";
import { ScreenSizeError } from "../../Errors/ScreenSizeError";
import { type BorderSettingsType, type BorderValues } from "./BorderSettingsDialog";
import { DefaultWrapper } from "./DefaultWrapper";
import { DragDropContainer } from "./DragDropContainer";
import { DragDropDragLayer } from "./DragDropDragLayer";
import { type DragDropManager } from "./DragDropManager";
import { DragDropMenu, type DragDropTabMenu } from "./DragDropMenu";
import { DragDropPreview } from "./DragDropPreview";
import { DragDropDataContext } from "./DragDropRenderer";
import {
    type ComponentStyles,
    type ComponentType,
    DRAG_DROP_ROOT_CONTAINER_ID,
    type DragDropEditItemDefinition,
    type DragDropEditItemMap,
    DragDropEditorContext,
    type DragDropEditorContextType,
    type DragDropWrapperProps,
    type InternalColumnComponentType,
} from "./DragDropTypes";
import { ColumnComponentEdit } from "./InternalComponents/Columns/ColumnComponentEdit";
import { ColumnComponentSettings } from "./InternalComponents/Columns/ColumnComponentSettings";
import { ImageComponent } from "./InternalComponents/Image/ImageComponent";
import { SpacerComponent } from "./InternalComponents/Spacer/SpacerComponent";
import { SpacerComponentEdit } from "./InternalComponents/Spacer/SpacerComponentEdit";
import { SpacerComponentSettings } from "./InternalComponents/Spacer/SpacerComponentSettings";
import { TextComponent } from "./InternalComponents/Text/TextComponent";
import { TextComponentEdit } from "./InternalComponents/Text/TextComponentEdit";
import { TextComponentSettings } from "./InternalComponents/Text/TextComponentSettings";
import { TextReplacement } from "./InternalComponents/Text/TextReplacement";

export const DEFAULT_BORDER_SIDE_SETTINGS: BorderValues = {
    borderColor: "#FFFFFF",
    borderStyle: "none",
    borderWidth: "0",
};

export const DEFAULT_BORDER_SETTINGS: BorderSettingsType = {
    top   : DEFAULT_BORDER_SIDE_SETTINGS,
    left  : DEFAULT_BORDER_SIDE_SETTINGS,
    right : DEFAULT_BORDER_SIDE_SETTINGS,
    bottom: DEFAULT_BORDER_SIDE_SETTINGS,
};

export const DEFAULT_PADDING_SETTINGS = { top: "", left: "", right: "", bottom: "" };

const DEFAULT_SETTINGS = {
    text: {
        background: "#FFFFFF",
        padding   : DEFAULT_PADDING_SETTINGS,
        border    : DEFAULT_BORDER_SETTINGS,
    },
    img: {
        image     : "",
        width     : "auto",
        height    : "auto",
        horizAlign: "center",
        background: "#FFFFFF",
        padding   : DEFAULT_PADDING_SETTINGS,
        border    : DEFAULT_BORDER_SETTINGS,
    },
    column: {
        padding: DEFAULT_PADDING_SETTINGS,
        border : DEFAULT_BORDER_SETTINGS,
    },
    spacer: {},
} satisfies Record<string, ComponentStyles>;

const DEFAULT_ITEMS: DragDropEditItemMap = {
    text: {
        component: TextComponent,
        edit     : TextComponentEdit,
        settings : TextComponentSettings,
        onCreate : () => ({
            value: "",
            style: {
                ...DEFAULT_SETTINGS.text,
            },
        }),
        name  : "Text",
        icon  : "font",
        styles: [ "background", "padding", "border" ],
    },
    img: {
        component: ImageComponent,
        settings : () => null,
        onCreate : () => ({
            value: "",
            style: {
                ...DEFAULT_SETTINGS.img,
            },
        }),
        name  : "Image",
        icon  : "image",
        styles: [ "image", "width", "height", "horizAlign", "background", "padding", "border" ],
    },
    internal_column: {
        component: ColumnComponentEdit,
        settings : ColumnComponentSettings,
        onCreate : ({ createContainer }) => ({
            value: [{
                id   : createContainer(),
                width: 0.5,
            }, {
                id   : createContainer(),
                width: 0.5,
            }],
            style: {
                ...DEFAULT_SETTINGS.column,
            },
        }),
        getContainers: (component) => {
            return component.value.map(column => column.id);
        },
        name  : "Columns",
        icon  : "columns",
        styles: [ "padding", "border" ],
    } as DragDropEditItemDefinition<Omit<InternalColumnComponentType, "type">>,
    spacer: {
        component: SpacerComponent,
        edit     : SpacerComponentEdit,
        settings : SpacerComponentSettings,
        onCreate : () => ({
            value: "100",
            style: {
                ...DEFAULT_SETTINGS.spacer,
            },
        }),
        name  : "Spacer",
        icon  : "arrows-alt-v",
        styles: [],
    },
};

export type DragDropDefaultItems = keyof typeof DEFAULT_ITEMS;

type OverrideItemDefinition = Partial<DragDropEditItemDefinition> | {
    disabled?: boolean;
};

export type DragDropDefaultOverride = Partial<Record<DragDropDefaultItems, OverrideItemDefinition>>;

interface Props {
    manager: DragDropManager;
    items: DragDropEditItemMap;
    override?: DragDropDefaultOverride;
    onCancel: () => void;
    onSave: (quit?: boolean) => void;
    title: ReactNode;
    wrapper?: ReactComponent<DragDropWrapperProps>;
    data: Record<string, unknown>;
    name: string;
}

export const DragDropEditor: FC<Props> = ({
    manager,
    items: propItems,
    override,
    onCancel,
    onSave,
    title,
    wrapper,
    data,
    name,
}) => {
    const dispatch = useShopfrontDispatch();

    const dirtyRef = useRef(false);

    const [ highlight, setHighlight ] = useState<null | string>(null);
    const [ selected, setSelected ] = useState<string | null>(null);
    const [ preview, setPreview ] = useState(false);
    const [ tab, setTab ] = useState<DragDropTabMenu>("components");

    const smallScreen = useMediaQuery("(max-width: 768px)");

    // Add the default drag drop items to the item map
    const items = useMemo<DragDropEditItemMap>(() => {
        const results: DragDropEditItemMap = { ...propItems };

        for(const key in DEFAULT_ITEMS) {
            let toOverride: Partial<DragDropEditItemDefinition> = {};

            if(override) {
                const itemOverride = override[key];

                if(itemOverride) {
                    // Check to see if item is disabled
                    if("disabled" in itemOverride) {
                        if(itemOverride.disabled) {
                            continue;
                        }
                    } else {
                        toOverride = itemOverride as Partial<DragDropEditItemDefinition>;
                    }
                }
            }

            results[key] = {
                ...DEFAULT_ITEMS[key],
                ...toOverride,
            };
        }

        return results;
    }, [ propItems, override ]);

    // Add drag drop update event listener
    useEffect(() => {
        const listener = () => {
            dirtyRef.current = true;
        };

        manager.addListener("update", listener);

        return () => {
            manager.removeListener("update", listener);
        };
    }, [ manager ]);

    // Create the array of accepted component keys
    const accept = useMemo(() => {
        return Object.keys(items);
    }, [ items ]);

    const onCreate = useCallback((type: string): ComponentType => {
        if(type in items) {
            return {
                ...items[type].onCreate({
                    createContainer: (components?: Array<string>) => {
                        return manager.createContainer(components);
                    },
                }),
                type,
            };
        }

        throw new Error(`Attempting to create component of invalid type "${type}"`);
    }, [ items, manager ]);

    const onConfigure = useCallback(() => {
        if(!selected) {
            return;
        }

        setTab("settings");
    }, [ selected ]);

    useEffect(() => {
        if(!selected) {
            setTab("components");
        } else {
            setTab("settings");
        }
    }, [ selected ]);

    const textReplacement = useMemo(() => {
        return new TextReplacement((data?.text as Record<string, unknown>) ?? {});
    }, [ data ]);

    const context = useMemo<DragDropEditorContextType>(() => ({
        manager,
        items,
        accept,
        selected,
        setSelected,
        onCreate,
        onConfigure,
        highlight,
        textReplacement,
    }), [
        manager,
        items,
        accept,
        selected,
        onCreate,
        onConfigure,
        highlight,
        textReplacement,
    ]);

    const onCancelClick = (e: MouseEvent<HTMLButtonElement>): void => {
        if(dirtyRef.current) {
            dispatch(showDialog(
                "Save",
                `Save ${name}?`,
                "question",
                [{
                    label   : "Yes",
                    classes : [ "button-save" ],
                    callback: () => {
                        dirtyRef.current = false;
                        onSave(true);
                    },
                }, {
                    label   : "No",
                    classes : [ "button-danger" ],
                    callback: () => {
                        dirtyRef.current = false;
                        onCancel();
                    },
                }, CancelOptionButton ]
            ));
        } else {
            onCancel();
        }

        // Prevent the flow up the body
        e.stopPropagation();
    };

    const Wrapper = typeof wrapper === "undefined" ? DefaultWrapper : wrapper;

    usePrompt(() => dirtyRef.current, "You have unsaved changed, are you sure you wish to leave?");

    if(smallScreen) {
        return (
            <ScreenSizeError />
        );
    }

    return (
        <DndProvider
            backend={TouchBackend}
            options={{
                enableMouseEvents: true,
            }}
        >
            <DragDropEditorContext.Provider value={context}>
                <div className="flex flex-row h-full">
                    <div
                        className={
                            "flex flex-col items-center bg-neutral-700 p-8 h-full overflow-auto grow " +
                            "shadow-inner"
                        }
                        onClick={() => {
                            setSelected(null);
                        }}
                    >
                        <div className="text-white flex items-center justify-center p-4">
                            {title}
                        </div>
                        <div className="border border-black text-black bg-white p-0">
                            <Wrapper
                                preview={preview}
                                data={data}
                            >
                                {preview ? (
                                    <DragDropPreview
                                        items={items}
                                        data={data}
                                    />
                                ) : (
                                    <DragDropDataContext.Provider value={data}>
                                        <DragDropContainer
                                            id={DRAG_DROP_ROOT_CONTAINER_ID}
                                        />
                                    </DragDropDataContext.Provider>
                                )}
                            </Wrapper>
                        </div>
                    </div>
                    <DragDropMenu
                        tab={tab}
                        onTabChange={setTab}
                        selected={selected}
                        preview={preview}
                        onPreviewClick={() => {
                            if(!preview) {
                                setSelected(null);
                            }

                            setPreview(prevState => !prevState);
                        }}
                        onCancelClick={onCancelClick}
                        onSaveClick={() => {
                            onSave();
                        }}
                        onHighlight={setHighlight}
                    />
                </div>
                <DragDropDragLayer />
            </DragDropEditorContext.Provider>
        </DndProvider>
    );
};
